import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Slider } from "./ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { inr, track } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useCurrency } from "../context/CurrencyContext";
import { APP_TYPES } from "../data/content";
import { ArrowRight } from "lucide-react";

const MODES = [
  { key: "standard", label: "STANDARD DELIVERY", tag: "in days" },
  { key: "specialist", label: "RUSH HOUR — VIBE CODING SPECIALIST", tag: "live session" },
  { key: "super", label: "RUSH HOUR — SUPER VIBE CODER", tag: "live session" },
];

export default function PricingCalculator() {
  const { settings } = useSite();
  const { currency, format, convert, fx, fxUnavailable } = useCurrency();
  const rates = settings.rates;
  const [mode, setMode] = useState("standard");
  const [appType, setAppType] = useState("Web Application");
  const [hours, setHours] = useState(25);
  const tracked = useRef(false);

  const totalINR = useMemo(() => (rates[mode] || 0) * hours, [rates, mode, hours]);
  const converted = currency === "INR" ? null : convert(totalINR);

  const onInteract = () => {
    if (!tracked.current) {
      tracked.current = true;
      track("calculator_use");
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-12" data-testid="pricing-calculator">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-8">
          <div>
            <label className="section-label block mb-4">DELIVERY MODE</label>
            <div className="flex flex-col gap-3">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  data-testid={`calc-mode-${m.key}`}
                  onClick={() => { setMode(m.key); onInteract(); }}
                  className={`text-left rounded-xl border px-5 py-4 transition-colors duration-200 ${
                    mode === m.key ? "border-flame bg-flame/10" : "border-white/10 bg-navy-700/40 hover:border-white/25"
                  }`}
                >
                  <span className="font-heading font-bold text-sm tracking-wide text-slate-100">{m.label}</span>
                  <span className="block text-xs text-slate-400 mt-1">
                    {inr(rates[m.key])}/hour · {m.tag}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="section-label block mb-4">APPLICATION TYPE</label>
            <Select value={appType} onValueChange={(v) => { setAppType(v); onInteract(); }}>
              <SelectTrigger data-testid="calc-app-type" className="bg-navy-700/60 border-white/10 h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-800 border-white/10">
                {APP_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-end justify-between mb-4">
              <label className="section-label">ESTIMATED HOURS</label>
              <span className="font-mono text-2xl font-bold text-flame" data-testid="calc-hours-value">{hours}h</span>
            </div>
            <Slider
              data-testid="calc-hours-slider"
              min={1}
              max={100}
              step={1}
              value={[hours]}
              onValueChange={(v) => { setHours(v[0]); onInteract(); }}
            />
            <div className="flex justify-between text-[10px] font-mono text-slate-500 mt-2">
              <span>1h</span><span>50h</span><span>100h</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center rounded-xl bg-navy-900/70 border border-white/5 p-8 sm:p-10">
          <p className="section-label">ILLUSTRATIVE ESTIMATE</p>
          <p className="mt-2 text-sm text-slate-400">
            {appType} · {MODES.find((m) => m.key === mode)?.label}
          </p>
          <p className="mt-6 font-heading text-5xl sm:text-6xl font-black tracking-tight text-white" data-testid="calc-total">
            {currency === "INR" || converted === null ? inr(totalINR) : format(converted)}
          </p>
          {currency !== "INR" && converted !== null && (
            <p className="mt-3 text-xs text-slate-400" data-testid="calc-converted-note">
              Base price {inr(totalINR)} · equivalent in {currency} at the current exchange rate.
            </p>
          )}
          {fxUnavailable && (
            <p className="mt-3 text-xs text-amber-400" data-testid="calc-fx-unavailable">
              Live currency conversion temporarily unavailable — showing base INR pricing.
            </p>
          )}
          <p className="mt-3 font-mono text-xs text-slate-500">
            {hours} hours × {inr(rates[mode])}/hour
          </p>
          {fx.updated_at && (
            <p className="mt-2 font-mono text-[10px] text-slate-600" data-testid="calc-fx-timestamp">
              Exchange rates updated: {new Date(fx.updated_at).toLocaleString("en-IN")}
            </p>
          )}
          <div className="mt-8 border-t border-white/10 pt-6 space-y-4">
            <p className="text-xs leading-relaxed text-slate-500">
              Illustrative estimate only. Hourly rates are fixed by delivery model; final project cost depends on
              estimated development hours and approved scope. The final estimate is confirmed after requirement analysis.
            </p>
            <Link
              to={`/checkout?service=${mode}&hours=${hours}`}
              data-testid="calc-checkout-link"
              className="inline-flex items-center gap-2 text-sm font-heading font-bold text-flame hover:gap-3 transition-all"
            >
              PROCEED TO CHECKOUT <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
